import { Button } from '@cellula/react';
import { useResolve } from '@visiba/instantiation';
import { AppointmentController } from '../view_appointment.controller';
import { withTranslation } from '@application/i18n/with_translation';
import { ViewAppointmentState } from '../view_appointment_state.component';
import type { LeaveType } from '@visiba/retina';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { useEffect } from 'react';

interface Props {
	ticket?: string;
	type: LeaveType;
}

export function ScreenReconnect(props: Props) {
	switch (props.type) {
		case 'mayEnd':
			return <ScreenMayEnd {...props} />;
		case 'mayReconnect':
			return <ScreenMayReconnect {...props} />;
		case 'multipleConnections':
			return <ScreenMultipleConnections {...props} />;
		case 'unexpectedlyDisconnected':
			return <ScreenUnexpectedlyDisconnected {...props} />;
		default:
			return <ScreenForceLeave {...props} />;
	}
}

function ScreenMultipleConnections(_: Props) {
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.service.trackPageView({
				customTitle: 'Left appointment: Multiple connections',
			});
		},
		[analyticCtx],
	);

	return (
		<ViewAppointmentState
			title={withTranslation('View.Appointment.ScreenReconnect.MultipleConnections.Title')}
			description={withTranslation('View.Appointment.ScreenReconnect.MultipleConnections.Description')}
			illustration='devices'
		/>
	);
}

function ScreenUnexpectedlyDisconnected(_: Props) {
	const controller = useResolve(AppointmentController);
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.service.trackPageView({
				customTitle: 'Left appointment: Unexpectedly disconnected',
			});
		},
		[analyticCtx],
	);

	function handleRejoinClick() {
		analyticCtx.trackEvent('Clicked CTA: Reconnect');
		controller.handleReconnect();
	}

	return (
		<ViewAppointmentState
			title={withTranslation('View.Appointment.ScreenReconnect.UnexpectedlyDisconnected.Title')}
			description={withTranslation('View.Appointment.ScreenReconnect.UnexpectedlyDisconnected.Description')}
			illustration='disconnected'
			actions={
				<Button variant='secondary' onClick={handleRejoinClick}>
					{withTranslation('View.Appointment.ScreenReconnect.CTA.Rejoin')}
				</Button>
			}
		/>
	);
}

function ScreenForceLeave(_: Props) {
	const controller = useResolve(AppointmentController);
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.service.trackPageView({
				customTitle: 'Left appointment: Force leave',
			});
		},
		[analyticCtx],
	);

	function handleForceEvaluationClick() {
		analyticCtx.trackEvent('Clicked CTA: Evaluation');
		controller.handleForceEvaluation();
	}

	return (
		<ViewAppointmentState
			title={withTranslation('View.Appointment.ScreenReconnect.ForcedLeft.Title')}
			description={withTranslation('View.Appointment.ScreenReconnect.ForcedLeft.Description')}
			illustration='door'
			actions={
				<Button variant='primary' onClick={handleForceEvaluationClick}>
					{withTranslation('View.Appointment.ScreenReconnect.CTA.Evaluation')}
				</Button>
			}
		/>
	);
}

function ScreenMayEnd(_: Props) {
	const controller = useResolve(AppointmentController);
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.service.trackPageView({
				customTitle: 'Left appointment: May end',
			});
		},
		[analyticCtx],
	);

	function handleRejoinClick() {
		analyticCtx.trackEvent('Clicked CTA: Reconnect');
		controller.handleReconnect();
	}

	function handleForceEndClick() {
		analyticCtx.trackEvent('Clicked CTA: End');

		return controller.handleForceEnd();
	}

	return (
		<ViewAppointmentState
			title={withTranslation('View.Appointment.ScreenReconnect.MayEnd.Title')}
			description={withTranslation('View.Appointment.ScreenReconnect.MayEnd.Description')}
			illustration='phoneOngoing'
			actions={
				<>
					<Button variant='primary' onClick={handleForceEndClick}>
						{withTranslation('View.Appointment.ScreenReconnect.CTA.End')}
					</Button>
					<Button variant='secondary' onClick={handleRejoinClick}>
						{withTranslation('View.Appointment.ScreenReconnect.CTA.Rejoin')}
					</Button>
				</>
			}
		/>
	);
}

function ScreenMayReconnect(_: Props) {
	const controller = useResolve(AppointmentController);
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.service.trackPageView({
				customTitle: 'Left appointment: May reconnect',
			});
		},
		[analyticCtx],
	);

	function handleRejoinClick() {
		analyticCtx.trackEvent('Clicked CTA: Reconnect');
		controller.handleReconnect();
	}

	function handleForceEvaluationClick() {
		analyticCtx.trackEvent('Clicked CTA: Evaluation');
		controller.handleForceEvaluation();
	}

	return (
		<ViewAppointmentState
			title={withTranslation('View.Appointment.ScreenReconnect.MayReconnect.Title')}
			description={withTranslation('View.Appointment.ScreenReconnect.MayReconnect.Description')}
			illustration='door'
			actions={
				<>
					<Button variant='primary' onClick={handleForceEvaluationClick}>
						{withTranslation('View.Appointment.ScreenReconnect.CTA.Evaluation')}
					</Button>
					<Button variant='secondary' onClick={handleRejoinClick}>
						{withTranslation('View.Appointment.ScreenReconnect.CTA.Rejoin')}
					</Button>
				</>
			}
		/>
	);
}
